import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Handshake,
  Users,
  Landmark,
  TrendingUp,
  Globe,
  ChevronDown,
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
} from "lucide-react";
import Reveal from "./Reveal";
import { services } from "../mock";

const iconMap = { Handshake, Users, Landmark, TrendingUp };

const CapabilitiesSection = () => {
  const [openId, setOpenId] = useState(services[0]?.id);

  return (
    <section
      className="bg-[#F6F7F9] py-14 md:py-20"
      data-testid="homepage-services-section"
    >
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <Reveal className="mb-10 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[#E4545F]">
              What We Do
            </span>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-[#0F2A4A] md:text-4xl">
              Where we create impact
            </h2>
            <p className="mt-4 text-lg font-light leading-relaxed text-slate-600">
              Four focused consulting services that turn cross-border ambition
              into results. Tap any service to see what you get.
            </p>
          </div>
          <Link
            to="/services"
            data-testid="view-all-services-link"
            className="group inline-flex shrink-0 items-center gap-2 rounded-full border border-[#0F2A4A]/15 px-6 py-3 text-sm font-semibold text-[#0F2A4A] transition-colors duration-300 hover:border-[#0F2A4A] hover:bg-[#0F2A4A] hover:text-white"
          >
            View all services
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
          </Link>
        </Reveal>

        <div className="space-y-4" data-testid="services-accordion">
          {services.map((s) => {
            const Icon = iconMap[s.icon] || Globe;
            const open = openId === s.id;
            return (
              <Reveal
                key={s.id}
                className="relative overflow-hidden rounded-2xl shadow-[0_14px_44px_-26px_rgba(15,42,74,0.55)]"
              >
                {/* Frosted-glass backdrop */}
                <img
                  src={s.image}
                  alt={s.title}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full scale-110 object-cover blur-[3px]"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0b2038]/97 via-[#0F2A4A]/92 to-[#0F2A4A]/80" />

                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setOpenId(open ? null : s.id)}
                    data-testid={`service-accordion-trigger-${s.id}`}
                    aria-expanded={open}
                    className="flex w-full items-center gap-4 px-6 py-5 text-left transition-colors duration-200 hover:bg-white/5 md:px-8"
                  >
                    <span
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ring-1 backdrop-blur-md transition-colors duration-300 ${
                        open
                          ? "bg-[#E4545F] text-white ring-[#E4545F]"
                          : "bg-white/10 text-white ring-white/25"
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="flex-1">
                      <span className="block text-base font-semibold leading-snug text-white md:text-lg">
                        {s.title}
                      </span>
                      <span className="mt-0.5 block text-[11px] font-medium uppercase tracking-[0.12em] text-[#F6A9AE]">
                        {s.category}
                      </span>
                    </span>
                    <ChevronDown
                      className={`h-5 w-5 shrink-0 text-white/70 transition-transform duration-300 ${
                        open ? "rotate-180 text-[#F6A9AE]" : ""
                      }`}
                    />
                  </button>

                  <div
                    className={`grid transition-all duration-300 ease-out ${
                      open
                        ? "grid-rows-[1fr] opacity-100"
                        : "grid-rows-[0fr] opacity-0"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div
                        className="px-6 pb-7 md:px-8 md:pl-[5.75rem]"
                        data-testid={`service-accordion-panel-${s.id}`}
                      >
                        <p className="text-sm leading-relaxed text-white/80">
                          {s.overview}
                        </p>
                        <div className="mt-5 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
                          {s.whatYouGet.map((w) => (
                            <div
                              key={w}
                              className="flex items-start gap-2.5 text-sm text-white/90"
                            >
                              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#F6A9AE]" />
                              <span>{w}</span>
                            </div>
                          ))}
                        </div>
                        <Link
                          to={`/services/${s.id}`}
                          data-testid={`service-accordion-link-${s.id}`}
                          className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-white transition-all duration-200 hover:gap-2.5"
                        >
                          Explore service
                          <ArrowUpRight className="h-4 w-4" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default CapabilitiesSection;
